import {
  Box,
  Button,
  ButtonGroup,
  ListItem,
  Popover,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  IconChevronRight,
  IconCloudDownload,
  IconFileAlert,
  IconLock,
  IconTrashX,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useUsuario } from "../../../../providers/usuarioProvider";
import Upload from "../../../../models/upload";
import { deleteDoc } from "firebase/firestore";
import UploadDb from "../../../../db/uploadDb";
import ChipCorretora from "../../../util/ui/chipCorretora";
import ChipTipoUpload from "../../../util/ui/chipTipoUpload";
import ChipExtensao from "../../../util/ui/chipExtensao";
import { getDownloadURL, getStorage, ref } from "firebase/storage";

type Props = {
  upload: Upload;
};
export default function UploadImportado({ upload }: Props) {
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Só decide a cor do número: warn quando o upload tem pendência,
  // accent-ink (tinta do accent, legível nos dois temas) quando está limpo.
  const atencao =
    !!upload.incompatibilidades ||
    (!!upload.quantidadeParciais && upload.quantidadeParciais > 0);

  const corDoNumero = (theme: Theme) => ({
    color: atencao ? theme.palette.warning.main : theme.palette.accentInk,
    fontWeight: 500,
  });

  return (
    <ListItem
      disableGutters
      sx={(theme) => ({
        px: 2,
        py: 1,
        cursor: "pointer",
        transition: theme.transitions.create("background-color", {
          duration: 120,
        }),
        "&:hover": { backgroundColor: theme.palette.ink["08"] },
      })}
      onClick={() => navigate(`/arquivo/upload/${upload.id}`)}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
        width="100%"
      >
        <Tooltip title={upload.nomeArquivoOriginal || ""} placement="top">
          <Box display="flex" flexGrow={1} px={1} alignItems="center" gap={1}>
            <Typography variant="body1" component="div" color="text.secondary">
              <Typography variant="dataMono" component="span" sx={corDoNumero}>
                {!!upload.itensImportados
                  ? upload.itensImportados.length
                  : upload.quantidadePregoes}
              </Typography>{" "}
              {!!upload.itensImportados
                ? upload.itensImportados.length > 1
                  ? "itens importados da "
                  : "item importado da "
                : upload.quantidadePregoes! > 1
                  ? "notas da "
                  : "nota da "}
              <ChipCorretora codigoCorretora={upload.corretora} /> entre{" "}
              <Typography variant="dataMono" component="span" sx={corDoNumero}>
                {upload.periodo!.de.format("DD/MM/YY")}
              </Typography>{" "}
              e{" "}
              <Typography variant="dataMono" component="span" sx={corDoNumero}>
                {upload.periodo!.a.format("DD/MM/YY")}
              </Typography>
              <Typography variant="body1" component="span">
                {upload.quantidadeParciais && upload.quantidadeParciais > 0 ? (
                  <>
                    {", +"}
                    <Typography
                      variant="dataMono"
                      component="span"
                      sx={(theme) => ({
                        color: theme.palette.warning.main,
                        fontWeight: 500,
                      })}
                    >
                      {upload.quantidadeParciais}
                    </Typography>
                    {" nota não lida."}
                  </>
                ) : (
                  "."
                )}
              </Typography>
            </Typography>
          </Box>
        </Tooltip>

        {upload.incompatibilidades ||
        (upload.quantidadeParciais && upload.quantidadeParciais > 0) ? (
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {upload.incompatibilidades && (
              <Box
                display="flex"
                flexDirection="column"
                gap={0.5}
                p={1.5}
                maxWidth={420}
              >
                <Typography
                  variant="dataMono"
                  sx={{ fontSize: 12, wordBreak: "break-word" }}
                >{`Incompatibilidades: ${JSON.stringify(
                  upload.incompatibilidades,
                )}`}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >{`Clique no ícone à direita para procurar as operações ignoradas.`}</Typography>
              </Box>
            )}
          </Popover>
        ) : (
          <></>
        )}

        <Box display="flex" gap={0.5} alignItems="center" mr={1}>
          {upload.senhaProtegida && (
            <Tooltip title="PDF protegido por senha" placement="top">
              <Box
                display="inline-flex"
                alignItems="center"
                color="text.secondary"
              >
                <IconLock size={16} />
              </Box>
            </Tooltip>
          )}
          <ChipTipoUpload tipo={upload.tipo} />
          <Box display="inline-flex" alignItems="center">
            <ChipExtensao nomeArquivo={upload.nomeArquivoOriginal} />
          </Box>
        </Box>
        <ButtonGroup
          size="small"
          variant="outlined"
          onClick={(e) => e.stopPropagation()}
        >
          {(upload.incompatibilidades ||
            (upload.quantidadeParciais && upload.quantidadeParciais > 0)) && (
            <Button
              size="small"
              color="warning"
              onMouseOver={(event) => setAnchorEl(event.currentTarget)}
            >
              <IconFileAlert size={16} />
            </Button>
          )}
          <Button
            color="info"
            size="small"
            onClick={async (e) => {
              e.stopPropagation();
              if (upload.arquivo) {
                const url = await getDownloadURL(
                  ref(getStorage(), upload.arquivo),
                );
                window.open(url, "_blank");
              }
            }}
          >
            <IconCloudDownload size={16} />
          </Button>
          <Button
            color="error"
            size="small"
            onClick={async (e) => {
              e.stopPropagation();
              if (!!visualizado)
                await deleteDoc(
                  new UploadDb().doc({
                    usuarioId: visualizado,
                    uploadId: upload.id,
                  }),
                );
            }}
          >
            <IconTrashX size={16} />
          </Button>
        </ButtonGroup>
        {/* A linha inteira navega para /arquivo/upload/{id}, onde ficam as notas
            deste upload. Sem esta seta nada sinalizava que a linha era clicável. */}
        <Box
          aria-hidden
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 20,
            height: 20,
            flexShrink: 0,
            ml: 0.5,
            color: "text.secondary",
          }}
        >
          <IconChevronRight size={16} strokeWidth={2.5} />
        </Box>
      </Box>
    </ListItem>
  );
}
